import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Grid } from "semantic-ui-react";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableContainer from "../../common/ui/table-container/TableContainer";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "../../common/ui/data-table/DataTable.css";
import "./Attendance.css";
import { AttendanceGuideButton, AttendanceHelpModal } from "./AttendanceHelp";
import {
  fetchChangeRequestsByEvent,
  fetchMySubmissionsPaged,
  handleAttendanceApiError,
  withdrawChangeRequest,
} from "./attendanceApi";
import {
  AttendanceChangeRequestPeriodMode,
  AttendanceChangeRequestStatus,
  AttendanceChangeRequestType,
  attendanceKindLabel,
  changeRequestListItemIsAwaitingApproval,
  changeRequestListItemStatusDisplay,
  workplaceTypeDisplayLabel,
} from "./attendanceConstants";
import { formatAttendanceTime } from "./attendanceDateUtils";
import {
  addDays,
  formatMonthRangeLabel,
  formatWeekRangeReadable,
  getWeekStartMonday,
  startOfMonth,
  toUtcDateOnlyISO,
} from "../timesheets/timesheetDateUtils";
import { Common } from "../../../utils";

const PAGE_SIZE = 20;

const PERIOD = {
  MONTHLY: "monthly",
  WEEKLY: "weekly",
};

/** Bootstrap badge class for change-request status in view modal */
const correctionStatusBadgeClass = (status) => {
  const s = String(status ?? "").toLowerCase();
  if (s === "pending" || Number(status) === 1) return "bg-warning text-dark";
  if (s === "approved" || Number(status) === 2) return "bg-success";
  if (s === "rejected" || Number(status) === 3) return "bg-danger";
  if (s === "withdrawn" || Number(status) === 4) return "bg-secondary";
  return "bg-light text-dark";
};

const correctionQueueStatusTagClass = (row) => {
  if (!row || typeof row !== "object") return "att-cr-queue-status-tag--unknown";
  const status = /** @type {{ status?: unknown }} */ (row).status;
  const s = String(status ?? "").toLowerCase();
  const n = Number(status);
  if (s === "pending" || n === AttendanceChangeRequestStatus.Pending) return "att-cr-queue-status-tag--pending";
  if (s === "approved" || n === AttendanceChangeRequestStatus.Approved) return "att-cr-queue-status-tag--approved";
  if (s === "rejected" || n === AttendanceChangeRequestStatus.Rejected) return "att-cr-queue-status-tag--rejected";
  if (s === "withdrawn" || n === AttendanceChangeRequestStatus.Withdrawn) return "att-cr-queue-status-tag--withdrawn";
  return "att-cr-queue-status-tag--unknown";
};

const optStr = (v) => {
  const s = String(v ?? "").trim();
  return s || "—";
};

const sanitizeDeleteSummaryForDisplay = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const cleanedLines = s
    .split(/\r?\n/)
    .map((line) => {
      const t = line.trim();
      if (!t) return "";
      if (/^event\s*id\s*:/i.test(t)) return "";
      if (/^user\s*attendance\s*event\s*id\s*:/i.test(t)) return "";
      if (/^attendance\s*event\s*id\s*:/i.test(t)) return "";
      return t
        .replace(/\s*event\s*id\s*:\s*[^\s]+/gi, "")
        .replace(/\s*user\s*attendance\s*event\s*id\s*:\s*[^\s]+/gi, "")
        .replace(/\s*attendance\s*event\s*id\s*:\s*[^\s]+/gi, "")
        .trim();
    })
    .filter(Boolean);
  return cleanedLines.join("\n").trim();
};

const AttendanceSubmittedRequestsPage = () => {
  const { t } = useTranslation();

  const [periodView, setPeriodView] = useState(PERIOD.MONTHLY);
  const [helpKey, setHelpKey] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [weekStart, setWeekStart] = useState(() => getWeekStartMonday(new Date()));

  const [officeFilter, setOfficeFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [offices, setOffices] = useState(/** @type {{ id: string | number, name: string }[]} */ ([]));
  const [departments, setDepartments] = useState(/** @type {{ id: string | number, name: string }[]} */ ([]));

  const [statusFilter, setStatusFilter] = useState("");
  const [requestTypeFilter, setRequestTypeFilter] = useState("");

  const [historyPage, setHistoryPage] = useState(1);
  const [historyData, setHistoryData] = useState(/** @type {Awaited<ReturnType<typeof fetchMySubmissionsPaged>> | null} */ (null));
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [actionId, setActionId] = useState(null);
  const [withdrawModal, setWithdrawModal] = useState(/** @type {{ requestId: string } | null} */ (null));

  const [viewModal, setViewModal] = useState(
    /** @type {{ userAttendanceEventId: string, previewName: string, previewEmail: string, loading: boolean, error: string | null, data: Awaited<ReturnType<typeof fetchChangeRequestsByEvent>> | null } | null} */ (
      null
    )
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { Department, Office } = config.REST_API;
        const [depRes, offRes] = await Promise.all([axiosBasic.get(Department.Get), axiosBasic.get(Office.GetCompanyOffices)]);
        const depBody = depRes.data || {};
        if (!cancelled && depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({ id: d.id, name: d.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
        const offBody = offRes.data || {};
        if (!cancelled && offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data
              .map((o) => ({ id: o.id, name: o.countryName ? `${o.name} (${o.countryName})` : o.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
      } catch {
        if (!cancelled) {
          setDepartments([]);
          setOffices([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const myListQuery = useMemo(() => {
    const base = {
      officeId: officeFilter || undefined,
      departmentId: departmentFilter || undefined,
      status: statusFilter || undefined,
      requestTypeFilter: requestTypeFilter || undefined,
    };

    if (periodView === PERIOD.MONTHLY) {
      return {
        ...base,
        periodMode: AttendanceChangeRequestPeriodMode.Monthly,
        year: monthAnchor.getFullYear(),
        month: monthAnchor.getMonth() + 1,
      };
    }

    const weekEnd = addDays(weekStart, 6);
    return {
      ...base,
      periodMode: AttendanceChangeRequestPeriodMode.Weekly,
      weekRangeStart: toUtcDateOnlyISO(weekStart),
      weekRangeEnd: toUtcDateOnlyISO(weekEnd),
    };
  }, [periodView, monthAnchor, weekStart, officeFilter, departmentFilter, statusFilter, requestTypeFilter]);

  useLayoutEffect(() => {
    setHistoryPage(1);
  }, [myListQuery]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchMySubmissionsPaged(historyPage, PAGE_SIZE, myListQuery);
      setHistoryData(res);
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.loadRequests"));
      setHistoryData(null);
    } finally {
      setLoadingHistory(false);
    }
  }, [historyPage, myListQuery, t]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!viewModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setViewModal(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewModal]);

  const shiftMonth = (delta) => {
    setMonthAnchor((m) => {
      const d = new Date(m);
      d.setMonth(d.getMonth() + delta);
      return startOfMonth(d);
    });
  };

  const shiftWeek = (delta) => {
    setWeekStart((w) => addDays(w, delta * 7));
  };

  const weekEndDate = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekLabel = formatWeekRangeReadable(weekStart, weekEndDate);

  const periodPillLabel = useMemo(
    () => (periodView === PERIOD.MONTHLY ? formatMonthRangeLabel(monthAnchor) : weekLabel),
    [periodView, monthAnchor, weekLabel]
  );

  const showJumpToCurrentPeriod = useMemo(() => {
    const now = new Date();
    const inCurrentMonth = monthAnchor.getFullYear() === now.getFullYear() && monthAnchor.getMonth() === now.getMonth();
    if (periodView === PERIOD.MONTHLY) return !inCurrentMonth;
    const tw = getWeekStartMonday(now);
    const onCurrentWeek = weekStart.getTime() === tw.getTime();
    return !inCurrentMonth || !onCurrentWeek;
  }, [monthAnchor, periodView, weekStart]);

  const jumpToCurrentPeriod = useCallback(() => {
    const now = new Date();
    setMonthAnchor(startOfMonth(now));
    setWeekStart(getWeekStartMonday(now));
  }, []);

  const clearSubmittedFilters = useCallback(() => {
    setOfficeFilter("");
    setDepartmentFilter("");
    setStatusFilter("");
    setRequestTypeFilter("");
  }, []);

  const stats = historyData?.statistics;
  const totalCount = historyData?.totalCount ?? 0;
  const pageSize = historyData?.pageSize ?? PAGE_SIZE;
  const showHistoryPagination = Boolean(historyData) && totalCount > 0;
  const paginationFrom = totalCount > 0 ? (historyPage - 1) * pageSize + 1 : 0;
  const paginationTo = totalCount > 0 ? Math.min(historyPage * pageSize, totalCount) : 0;

  const afterMutation = useCallback(async () => {
    await loadHistory();
  }, [loadHistory]);

  const confirmWithdraw = async () => {
    if (!withdrawModal) return;
    const { requestId } = withdrawModal;
    setActionId(requestId);
    try {
      await withdrawChangeRequest(requestId);
      Common.showSuccessMessage(t("app.attendance.changeRequests.successWithdraw"));
      setWithdrawModal(null);
      await afterMutation();
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.loadRequests"));
    } finally {
      setActionId(null);
    }
  };

  const openViewDetail = useCallback(
    async (r) => {
      const eventId = String(r.userAttendanceEventId ?? "").trim();
      if (!eventId) {
        Common.showErrorMessage(t("app.attendance.correctionsQueue.viewMissingEventId"));
        return;
      }
      const previewName = String(r.employeeName ?? "").trim() || "—";
      const previewEmail = String(r.requestedByEmail ?? "").trim() || "—";
      setViewModal({
        userAttendanceEventId: eventId,
        previewName,
        previewEmail,
        loading: true,
        error: null,
        data: null,
      });
      try {
        const data = await fetchChangeRequestsByEvent(eventId, { includeAllHistory: true });
        setViewModal((m) =>
          m && m.userAttendanceEventId === eventId ? { ...m, loading: false, data: data ?? { userAttendanceEventId: eventId, requests: [] } } : m
        );
      } catch (e) {
        const msg =
          e?.response?.data?.message ??
          e?.response?.data?.Message ??
          e?.message ??
          t("app.attendance.correctionsQueue.viewDetailLoadFailed");
        setViewModal((m) => (m && m.userAttendanceEventId === eventId ? { ...m, loading: false, error: String(msg) } : m));
      }
    },
    [t]
  );

  const rows = historyData?.items ?? [];

  return (
    <div className="documents-page att-cr-page att-cr-queue-page att-sr-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-send-check" />}
        title={t("app.attendance.submittedRequests.title")}
        description={t("app.attendance.submittedRequests.subtitle")}
        rightContent={<AttendanceGuideButton onClick={() => setHelpKey("submittedRequests")} />}
      />

      <Grid stackable className="att-cr-grid">
        <Grid.Row>
          <Grid.Column width={16}>
            <section className="ts-appr-stats att-cr-stats att-cr-queue-stats" aria-label={t("app.attendance.changeRequests.statsAria")}>
              <div className="ts-appr-stat-card ts-appr-stat-card--total">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-collection" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loadingHistory && !stats ? "…" : stats?.totalRequests ?? 0}</span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.correctionsQueue.statTotalRequests")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-appr-stat-card ts-appr-stat-card--awaiting">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-hourglass-split" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loadingHistory && !stats ? "…" : stats?.pending ?? 0}</span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.changeRequests.statPending")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-appr-stat-card ts-appr-stat-card--approved">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-check-circle-fill" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loadingHistory && !stats ? "…" : stats?.approved ?? 0}</span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.changeRequests.statApproved")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-appr-stat-card ts-appr-stat-card--rejected">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-x-circle-fill" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loadingHistory && !stats ? "…" : stats?.rejected ?? 0}</span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.changeRequests.statRejected")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ts-appr-stat-card ts-appr-stat-card--withdrawn att-cr-stat-withdrawn">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-arrow-counterclockwise" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loadingHistory && !stats ? "…" : stats?.withdrawn ?? 0}</span>
                      <span className="ts-appr-stat-card__label">{t("app.attendance.changeRequests.statWithdrawn")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <section className="ts-time-log-shell att-cr-queue-filters" aria-label={t("app.attendance.submittedRequests.filtersAria")}>
              <div className="ts-appr-filters" role="search">
                <div className="ts-appr-filters__title text-muted" id="att-sr-filters-heading">
                  {t("app.attendance.submittedRequests.filtersToolbarLabel")}
                </div>

                <div className="ts-appr-filters__toolbar" aria-labelledby="att-sr-filters-heading">
                  <div className="ts-appr-filters__toolbar-left">
                    <div
                      className="ts-view-tabs ts-view-tabs--two ts-view-tabs--timelog-inline"
                      role="tablist"
                      aria-label={t("app.timesheets.timeLog.viewMode")}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={periodView === PERIOD.MONTHLY}
                        className={periodView === PERIOD.MONTHLY ? "is-active" : ""}
                        onClick={() => {
                          setPeriodView(PERIOD.MONTHLY);
                          setMonthAnchor(startOfMonth(weekStart));
                        }}
                      >
                        {t("app.timesheets.timeLog.viewMonthly")}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={periodView === PERIOD.WEEKLY}
                        className={periodView === PERIOD.WEEKLY ? "is-active" : ""}
                        onClick={() => {
                          setPeriodView(PERIOD.WEEKLY);
                          setWeekStart(getWeekStartMonday(monthAnchor));
                        }}
                      >
                        {t("app.timesheets.timeLog.viewWeekly")}
                      </button>
                    </div>
                    <span className="ts-appr-filters__toolbar-divider d-none d-sm-block" aria-hidden />

                    <div className="ts-tl-period-toolbar-group ts-appr-filters__period-wrap">
                      <div
                        className="ts-period-cluster ts-period-cluster--inline ts-period-cluster--compact"
                        aria-label={t("app.timesheets.timeLog.periodRangeAria")}
                      >
                        <button
                          type="button"
                          onClick={() => (periodView === PERIOD.MONTHLY ? shiftMonth(-1) : shiftWeek(-1))}
                          aria-label={
                            periodView === PERIOD.MONTHLY
                              ? t("app.timesheets.reports.periodPrevMonth")
                              : t("app.timesheets.reports.periodPrevWeek")
                          }
                        >
                          <i className="bi bi-chevron-left" aria-hidden />
                        </button>
                        <span
                          className="ts-period-pill ts-period-pill--inline ts-period-pill--compact"
                          title={periodView === PERIOD.WEEKLY ? formatMonthRangeLabel(monthAnchor) : undefined}
                        >
                          <i className="bi bi-calendar3 text-primary" aria-hidden />
                          {periodPillLabel}
                        </span>
                        <button
                          type="button"
                          onClick={() => (periodView === PERIOD.MONTHLY ? shiftMonth(1) : shiftWeek(1))}
                          aria-label={
                            periodView === PERIOD.MONTHLY
                              ? t("app.timesheets.reports.periodNextMonth")
                              : t("app.timesheets.reports.periodNextWeek")
                          }
                        >
                          <i className="bi bi-chevron-right" aria-hidden />
                        </button>
                      </div>
                      {showJumpToCurrentPeriod ? (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm ts-tl-jump-current"
                          onClick={jumpToCurrentPeriod}
                          aria-label={
                            periodView === PERIOD.MONTHLY
                              ? t("app.timesheets.timeLog.jumpToCurrentMonthAria")
                              : t("app.timesheets.timeLog.jumpToCurrentWeekToolbarAria")
                          }
                        >
                          <i className="bi bi-calendar-check me-1" aria-hidden />
                          {periodView === PERIOD.MONTHLY
                            ? t("app.timesheets.timeLog.jumpToCurrentMonth")
                            : t("app.timesheets.reports.periodThisWeek")}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <p className="ts-appr-filters__toolbar-hint ts-appr-controls__hint">
                    {periodView === PERIOD.MONTHLY
                      ? t("app.timesheets.approvals.monthScopeHint")
                      : t("app.timesheets.approvals.weekScopeHint")}
                  </p>
                </div>

                <div className="ts-appr-filters__fields">
                  <div className="ts-filter-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-sr-queue-dept">
                      {t("app.timesheets.approvals.filterDepartment")}
                    </label>
                    <select id="att-sr-queue-dept" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                      <option value="">{t("app.attendance.correctionsQueue.filterAllDepartments")}</option>
                      {departments.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ts-filter-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-sr-queue-office">
                      {t("app.timesheets.approvals.filterOffice")}
                    </label>
                    <select id="att-sr-queue-office" value={officeFilter} onChange={(e) => setOfficeFilter(e.target.value)}>
                      <option value="">{t("app.attendance.correctionsQueue.filterAllOffices")}</option>
                      {offices.map((o) => (
                        <option key={o.id} value={String(o.id)}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ts-filter-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-sr-queue-type">
                      {t("app.attendance.correctionsQueue.filterRequestType")}
                    </label>
                    <select id="att-sr-queue-type" value={requestTypeFilter} onChange={(e) => setRequestTypeFilter(e.target.value)}>
                      <option value="">{t("app.attendance.correctionsQueue.typeAll")}</option>
                      <option value="Edit">{t("app.attendance.correctionsQueue.typeChangeRequest")}</option>
                      <option value="Delete">{t("app.attendance.correctionsQueue.typeTimeRemoval")}</option>
                    </select>
                  </div>
                  <div className="ts-filter-field ts-appr-filters__filter--status">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-sr-queue-status">
                      {t("app.timesheets.approvals.filterStatusLabel")}
                    </label>
                    <select id="att-sr-queue-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="">{t("app.timesheets.approvals.filterAnyStatus")}</option>
                      <option value="Pending">{t("app.attendance.changeRequests.statPending")}</option>
                      <option value="Approved">{t("app.attendance.changeRequests.statApproved")}</option>
                      <option value="Rejected">{t("app.attendance.changeRequests.statRejected")}</option>
                      <option value="Withdrawn">{t("app.attendance.changeRequests.statWithdrawn")}</option>
                    </select>
                  </div>
                  <div className="ts-filter-field ts-appr-filters__clear-field">
                    <label className="ts-tl-filter-bar-label text-muted" htmlFor="att-sr-clear">
                      {t("app.timesheets.approvals.clearFiltersLabel")}
                    </label>
                    <button
                      id="att-sr-clear"
                      type="button"
                      className="btn btn-outline-secondary ts-tl-clear-filters-btn ts-appr-filters__clear-btn"
                      onClick={clearSubmittedFilters}
                    >
                      <i className="bi bi-x-lg me-1" aria-hidden />
                      {t("app.timesheets.approvals.clearFilters")}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column width={16}>
            <div className="att-table-card att-cr-queue-table-wrap ts-table-wrap ts-table-wrap--approvals">
              <TableContainer>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 att-cr-queue-table">
                    <thead>
                      <tr>
                        <th scope="col">{t("app.attendance.changeRequests.colStatus")}</th>
                        <th scope="col">{t("app.attendance.changeRequests.colType")}</th>
                        <th scope="col">{t("app.attendance.changeRequests.colEvent")}</th>
                        <th scope="col">{t("app.attendance.changeRequests.colRequested")}</th>
                        <th scope="col">{t("app.attendance.changeRequests.colActions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingHistory && rows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-5 text-muted">
                            <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden />
                            {t("app.common.loading")}
                          </td>
                        </tr>
                      ) : null}
                      {!loadingHistory && rows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-5 text-muted">
                            {t("app.attendance.changeRequests.emptyHistory")}
                          </td>
                        </tr>
                      ) : null}
                      {rows.map((r) => (
                        <tr key={r.requestId}>
                          <td>
                            <span className={`att-cr-queue-status-tag ${correctionQueueStatusTagClass(r)}`}>
                              {changeRequestListItemStatusDisplay(r)}
                            </span>
                          </td>
                          <td>
                            {r.requestType === AttendanceChangeRequestType.Delete
                              ? t("app.attendance.correctionsQueue.typeTimeRemoval")
                              : t("app.attendance.correctionsQueue.typeChangeRequest")}
                          </td>
                          <td className="small">
                            {r.requestType === AttendanceChangeRequestType.Delete ? (
                              r.eventOccurredAtUtc ? (
                                <span>
                                  {attendanceKindLabel(r.eventType)} · {formatAttendanceTime(r.eventOccurredAtUtc)}
                                </span>
                              ) : (
                                <span className="text-muted">{t("app.attendance.correctionsQueue.eventTimeRemovalShort")}</span>
                              )
                            ) : r.eventOccurredAtUtc ? (
                              <span>
                                {attendanceKindLabel(r.eventType)} · {formatAttendanceTime(r.eventOccurredAtUtc)}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="text-nowrap small">{r.submittedAtUtc ? formatAttendanceTime(r.submittedAtUtc) : "—"}</td>
                          <td className="att-cr-appr-cell att-cr-queue-actions-td">
                            <div
                              className="att-cr-queue-actions-row ts-appr-decision-group att-sr-queue-actions"
                              role="group"
                              aria-label={t("app.attendance.submittedRequests.rowActionsAria")}
                            >
                              <button
                                type="button"
                                className="documents-grid-action-btn ts-appr-btn-view-detail ts-appr-decision-btn att-cr-queue-action-view"
                                onClick={() => openViewDetail(r)}
                                aria-label={t("app.attendance.correctionsQueue.viewRequest")}
                              >
                                {t("app.attendance.correctionsQueue.viewRequest")}
                              </button>
                              {changeRequestListItemIsAwaitingApproval(r) ? (
                                <button
                                  type="button"
                                  className="documents-grid-action-btn documents-grid-action-btn--delete ts-appr-decision-btn"
                                  disabled={actionId === r.requestId}
                                  onClick={() => setWithdrawModal({ requestId: r.requestId })}
                                >
                                  {t("app.attendance.changeRequests.withdraw")}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TableContainer>
              {showHistoryPagination ? (
                <nav className="att-cr-queue-pagination" aria-label={t("app.attendance.correctionsQueue.paginationAria")}>
                  <span className="att-cr-queue-pagination__range">
                    {t("app.attendance.correctionsQueue.paginationRange", {
                      from: paginationFrom,
                      to: paginationTo,
                      total: totalCount,
                    })}
                  </span>
                  <div
                    className="att-cr-queue-pagination__controls"
                    role="group"
                    aria-label={t("app.attendance.correctionsQueue.paginationNavAria")}
                  >
                    <button
                      type="button"
                      className="att-cr-queue-pagination__btn"
                      disabled={historyPage <= 1 || loadingHistory}
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      aria-label={t("app.timesheets.timeLog.paginationPrev")}
                    >
                      <i className="bi bi-chevron-left" aria-hidden />
                    </button>
                    <span className="att-cr-queue-pagination__divider" aria-hidden />
                    <button
                      type="button"
                      className="att-cr-queue-pagination__btn"
                      disabled={loadingHistory || historyPage * pageSize >= totalCount}
                      onClick={() => setHistoryPage((p) => p + 1)}
                      aria-label={t("app.timesheets.timeLog.paginationNext")}
                    >
                      <i className="bi bi-chevron-right" aria-hidden />
                    </button>
                  </div>
                </nav>
              ) : null}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {withdrawModal && (
        <div className="att-modal-overlay" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setWithdrawModal(null)}>
          <div className="att-modal" style={{ maxWidth: 420 }}>
            <div className="att-modal-header">
              <h2 className="att-modal-title mb-0">{t("app.attendance.changeRequests.withdrawTitle")}</h2>
              <button type="button" className="att-modal-close" onClick={() => setWithdrawModal(null)}>
                ×
              </button>
            </div>
            <div className="att-modal-body">
              <p className="mb-0 text-muted small">{t("app.attendance.changeRequests.withdrawLead")}</p>
            </div>
            <div className="att-modal-footer">
              <button type="button" className="att-btn att-btn--ghost" onClick={() => setWithdrawModal(null)}>
                {t("app.common.cancel")}
              </button>
              <button type="button" className="btn btn-outline-danger" disabled={!!actionId} onClick={confirmWithdraw}>
                {t("app.attendance.changeRequests.withdrawConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewModal ? (
        <div
          className="att-modal-overlay"
          role="presentation"
          onMouseDown={(e) => e.target === e.currentTarget && setViewModal(null)}
        >
          <div
            className="att-modal att-cr-view-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="att-sr-view-title"
            style={{ maxWidth: 680 }}
          >
            <div className="att-modal-header att-cr-view-modal__header att-cr-view-modal__header--hero">
              <div className="att-cr-view-modal__header-icon" aria-hidden>
                <i className="bi bi-inboxes" />
              </div>
              <div className="att-cr-view-modal__header-text flex-grow-1 min-w-0">
                <p className="att-cr-view-modal__eyebrow mb-0">{t("app.attendance.correctionsQueue.viewEventRequestsEyebrow")}</p>
                <h2 className="att-modal-title att-cr-view-modal__title mb-0" id="att-sr-view-title">
                  {t("app.attendance.correctionsQueue.viewEventRequestsTitle")}
                </h2>
                <p
                  className="att-cr-view-modal__sub mb-0 text-truncate"
                  title={`${viewModal.previewName} · ${viewModal.previewEmail}`}
                >
                  <span className="att-cr-view-modal__sub-name">{viewModal.previewName}</span>
                  <span className="att-cr-view-modal__sub-sep"> · </span>
                  <span className="att-cr-view-modal__sub-email">{viewModal.previewEmail}</span>
                </p>
              </div>
              <button type="button" className="att-modal-close att-cr-view-modal__close" onClick={() => setViewModal(null)} aria-label={t("app.common.close")}>
                ×
              </button>
            </div>
            <div className="att-modal-body att-cr-view-modal__body">
              {viewModal.loading ? (
                <div className="text-center py-5">
                  <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                  <span className="visually-hidden">{t("app.common.loading")}</span>
                </div>
              ) : null}
              {viewModal.error ? (
                <div className="alert alert-danger small mb-0" role="alert">
                  {viewModal.error}
                </div>
              ) : null}
              {!viewModal.loading && !viewModal.error && viewModal.data ? (
                <>
                  <div className="att-cr-view-intro" role="note">
                    <i className="bi bi-info-circle" aria-hidden />
                    <p className="mb-0">{t("app.attendance.correctionsQueue.viewRequestHistoryIntro")}</p>
                  </div>
                  {viewModal.data.requests.length === 0 ? (
                    <p className="text-muted small mb-0">{t("app.attendance.correctionsQueue.viewNoRequests")}</p>
                  ) : (
                    <ul className="list-unstyled att-cr-view-request-list mb-0">
                      {viewModal.data.requests.map((req) => {
                        const isDelete = Number(req.requestType) === AttendanceChangeRequestType.Delete;
                        const isEdit = Number(req.requestType) === AttendanceChangeRequestType.Edit;
                        const deleteSummaryDisplay = sanitizeDeleteSummaryForDisplay(req.deleteRequestSummary);
                        return (
                          <li key={req.requestId || `${req.submittedAtUtc}-${req.requestType}`}>
                            <article className="att-cr-view-request-card">
                              <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <span className={`badge ${isDelete ? "bg-secondary" : "bg-primary"}`}>
                                    {isDelete
                                      ? t("app.attendance.correctionsQueue.typeTimeRemoval")
                                      : t("app.attendance.correctionsQueue.typeChangeRequest")}
                                  </span>
                                  <span className={`badge ${correctionStatusBadgeClass(req.statusLabel || req.status)}`}>
                                    {changeRequestListItemStatusDisplay(req)}
                                  </span>
                                </div>
                                <span className="small text-muted text-nowrap">
                                  {t("app.attendance.changeRequests.colRequested")}:{" "}
                                  {req.submittedAtUtc ? formatAttendanceTime(req.submittedAtUtc) : "—"}
                                </span>
                              </div>
                              {req.processedAtUtc ? (
                                <p className="small text-muted mb-2 mb-md-3">
                                  {t("app.attendance.correctionsQueue.viewProcessedAt")}: {formatAttendanceTime(req.processedAtUtc)}
                                </p>
                              ) : null}

                              {isEdit ? (
                                <div className="row g-2 g-md-3">
                                  <div className="col-md-6">
                                    <div className="att-cr-view-compare att-cr-view-compare--before">
                                      <div className="att-cr-view-compare__title">{t("app.attendance.correctionsQueue.viewBaseline")}</div>
                                      <dl className="att-cr-view-dl small mb-0">
                                        <dt>{t("app.attendance.correctionsQueue.viewTime")}</dt>
                                        <dd>{req.previousOccurredAtUtc ? formatAttendanceTime(req.previousOccurredAtUtc) : "—"}</dd>
                                        <dt>{t("app.attendance.correctionsQueue.viewWorkplace")}</dt>
                                        <dd>{workplaceTypeDisplayLabel(req.previousWorkplaceType)}</dd>
                                        <dt>{t("app.attendance.correctionsQueue.viewLocation")}</dt>
                                        <dd>{optStr(req.previousLocation)}</dd>
                                        <dt>{t("app.attendance.correctionsQueue.viewNote")}</dt>
                                        <dd>{optStr(req.previousNote)}</dd>
                                      </dl>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="att-cr-view-compare att-cr-view-compare--after">
                                      <div className="att-cr-view-compare__title">{t("app.attendance.correctionsQueue.viewProposed")}</div>
                                      <dl className="att-cr-view-dl small mb-0">
                                        <dt>{t("app.attendance.correctionsQueue.viewTime")}</dt>
                                        <dd>{req.newOccurredAtUtc ? formatAttendanceTime(req.newOccurredAtUtc) : "—"}</dd>
                                        <dt>{t("app.attendance.correctionsQueue.viewWorkplace")}</dt>
                                        <dd>{workplaceTypeDisplayLabel(req.newWorkplaceType)}</dd>
                                        <dt>{t("app.attendance.correctionsQueue.viewLocation")}</dt>
                                        <dd>{optStr(req.newLocation)}</dd>
                                        <dt>{t("app.attendance.correctionsQueue.viewNote")}</dt>
                                        <dd>{optStr(req.newNote)}</dd>
                                      </dl>
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              {isDelete ? (
                                <div className="att-cr-view-compare att-cr-view-compare--single">
                                  <div className="att-cr-view-compare__title">{t("app.attendance.correctionsQueue.viewRemovalTarget")}</div>
                                  <dl className="att-cr-view-dl small mb-0">
                                    <dt>{t("app.attendance.changeRequests.colEvent")}</dt>
                                    <dd>
                                      {req.eventOccurredAtUtc
                                        ? `${attendanceKindLabel(req.eventType)} · ${formatAttendanceTime(req.eventOccurredAtUtc)}`
                                        : "—"}
                                    </dd>
                                    <dt>{t("app.attendance.correctionsQueue.viewWorkplace")}</dt>
                                    <dd>{workplaceTypeDisplayLabel(req.eventWorkplaceType)}</dd>
                                    {deleteSummaryDisplay ? (
                                      <>
                                        <dt>{t("app.attendance.correctionsQueue.viewSummary")}</dt>
                                        <dd className="text-break">{deleteSummaryDisplay}</dd>
                                      </>
                                    ) : null}
                                  </dl>
                                </div>
                              ) : null}

                              {!isEdit && !isDelete ? (
                                <p className="small text-muted mb-0">{t("app.attendance.correctionsQueue.viewGenericRequest")}</p>
                              ) : null}

                              {optStr(req.approverName) !== "—" || optStr(req.approverComment) !== "—" ? (
                                <div className="att-cr-view-approver mt-3 pt-3 border-top">
                                  {optStr(req.approverName) !== "—" ? (
                                    <div className="small text-muted mb-2">
                                      {t("app.attendance.correctionsQueue.colApprover")}: <strong>{String(req.approverName).trim()}</strong>
                                    </div>
                                  ) : null}
                                  {optStr(req.approverComment) !== "—" ? (
                                    <>
                                      <div className="small text-uppercase text-muted fw-bold mb-1">
                                        {t("app.attendance.changeRequests.comment")}
                                      </div>
                                      <p className="small mb-0 text-break">{String(req.approverComment).trim()}</p>
                                    </>
                                  ) : null}
                                </div>
                              ) : null}
                            </article>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              ) : null}
            </div>
            <div className="att-modal-footer">
              <button type="button" className="att-btn att-btn--ghost" onClick={() => setViewModal(null)}>
                {t("app.common.close")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <AttendanceHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AttendanceSubmittedRequestsPage;
